import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Autocomplete, Checkbox, TextField, Typography, Chip, Popover, Box } from '@mui/material';
import { connect, useDispatch } from 'react-redux';
import { fetchWowAdvertiserQuarters, resetWowData } from '../../redux/wow/wowActions';
import { SET_SELECTED_WOW_QUARTERS, SET_WOW_START_DATE, SET_WOW_END_DATE, SET_WOW_QUARTER_RANGES } from '../../redux/wow/wowTypes';
import { getMultipleQuartersDateRange, getQuarterDateRange } from '../../utils/quarterUtils';
import { fetchCampaignsForQuarters } from '../../redux/advertiser/advertiserActions'; // ⭐ Import directly
import { CLEAR_CAMPAIGN_LIST, SET_WOW_CAMPAIGN_LOADING } from '../../redux/advertiser/advertiserTypes';
import { resetScreenData } from '../../redux/campaign/campaignActions';
import { resetReportData } from '../../redux/reporting/reportingActions';
import { resetCreativeData } from '../../redux/creative/creativeActions';

const WowQuarterDropDown = ({
  fetchWowAdvertiserQuarters,
  advertiser_quarters,
  advertiser_guid,
  selected_wow_quarters,
  isSkanPresent,
  campaign_list,
  sfId,
  resetScreenData,
  resetReportData,
  resetCreativeData,
  resetWowData
}) => {
  const [wowQuarterss, setWowQuarterss] = useState([]);
  const [wowQuartersError, setWowQuartersError] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const [disabled, setDisabled] = useState(false);
  const dispatch = useDispatch();
  const dropdownRef = useRef(null);
  const previousAdvertiserGuid = useRef(advertiser_guid);
  const hasAutoSelectedRef = useRef(false);
  const abortControllerRef = useRef(null);
  const debounceTimerRef = useRef(null);

  // Helper to cancel previous request
  const cancelPreviousRequest = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      console.log('🚫 Cancelled previous campaign fetch');
    }
    abortControllerRef.current = new AbortController();
    return abortControllerRef.current.signal;
  }, []);

  // Debounced fetch function
  const debouncedFetchCampaigns = useCallback(
    (quarterRanges, isSkan) => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      const signal = cancelPreviousRequest();

      debounceTimerRef.current = setTimeout(() => {
        console.log('🔍 Debounced fetch: Fetching campaigns for quarters');

        // Set loading BEFORE fetch
        // dispatch({ type: SET_WOW_CAMPAIGN_LOADING });

        dispatch(fetchCampaignsForQuarters(advertiser_guid, sfId, quarterRanges, isSkan, signal));
      }, 300);
    },
    [advertiser_guid, sfId, dispatch, cancelPreviousRequest]
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  // Reset when advertiser changes
  useEffect(() => {
    if (previousAdvertiserGuid.current !== advertiser_guid) {
      console.log('🔄 Advertiser changed! Resetting WoW quarters...');

      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      setWowQuarterss([]);
      setWowQuartersError('');
      hasAutoSelectedRef.current = false;

      dispatch({ type: SET_SELECTED_WOW_QUARTERS, payload: { selected_wow_quarter: [] } });
      dispatch({ type: SET_WOW_START_DATE, payload: null });
      dispatch({ type: SET_WOW_END_DATE, payload: null });
      dispatch({ type: SET_WOW_QUARTER_RANGES, payload: [] });
      dispatch({ type: CLEAR_CAMPAIGN_LIST });

      previousAdvertiserGuid.current = advertiser_guid;
      fetchWowAdvertiserQuarters();
    }
  }, [advertiser_guid, dispatch, fetchWowAdvertiserQuarters]);

  useEffect(() => {
    if (advertiser_quarters.length === 0) {
      fetchWowAdvertiserQuarters();
    }
  }, [isSkanPresent, fetchWowAdvertiserQuarters, advertiser_quarters.length]);

  const setWowQuarters = (data) => {
    dispatch({
      type: SET_SELECTED_WOW_QUARTERS,
      payload: {
        selected_wow_quarter: data
      }
    });
  };

  useEffect(() => {
    if (advertiser_quarters.length === 0) {
      setDisabled(true);
    } else {
      setDisabled(false);
    }
  }, [advertiser_quarters]);

  // Auto-select quarters
  useEffect(() => {
    if (advertiser_quarters.length > 0 && wowQuarterss.length === 0 && advertiser_guid && !hasAutoSelectedRef.current) {
      console.log('📋 Auto-selecting quarters for advertiser:', advertiser_guid);
      hasAutoSelectedRef.current = true;

      //CLEAR T-2/T-1 campaigns FIRST (before fetching quarters)
      dispatch({ type: CLEAR_CAMPAIGN_LIST });

      setWowQuarterss(advertiser_quarters);
      setWowQuarters(advertiser_quarters);

      const { startDate, endDate } = getMultipleQuartersDateRange(advertiser_quarters);
      const quarterRanges = advertiser_quarters.map((quarter) => {
        const range = getQuarterDateRange(quarter);
        return { quarter, startDate: range.startDate, endDate: range.endDate };
      });

      dispatch({ type: SET_WOW_START_DATE, payload: startDate });
      dispatch({ type: SET_WOW_END_DATE, payload: endDate });
      dispatch({ type: SET_WOW_QUARTER_RANGES, payload: quarterRanges });

      // No debounce for auto-select
      const signal = cancelPreviousRequest();
      dispatch(fetchCampaignsForQuarters(advertiser_guid, sfId, quarterRanges, isSkanPresent, signal));
    }
  }, [advertiser_quarters, wowQuarterss.length, advertiser_guid, sfId, dispatch, isSkanPresent, cancelPreviousRequest]);

  // Clear error when quarters are selected
  useEffect(() => {
    if (wowQuarterss.length > 0 && wowQuartersError) {
      setWowQuartersError('');
    }
  }, [wowQuarterss.length, wowQuartersError]);

  const handleOpenPopover = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClosePopover = () => {
    setAnchorEl(null);
  };

  // Handle manual quarter change
  const handleChange = (event, newValue) => {
    if (newValue.length > 0) {
      setWowQuartersError('');
    }

    if (newValue.length === 0) {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      setWowQuartersError('Please select at least one Quarter.');
      dispatch({ type: SET_WOW_START_DATE, payload: null });
      dispatch({ type: SET_WOW_END_DATE, payload: null });
      dispatch({ type: SET_WOW_QUARTER_RANGES, payload: [] });
      dispatch({ type: CLEAR_CAMPAIGN_LIST });

      dispatch(resetScreenData());
      dispatch(resetReportData());
      dispatch(resetCreativeData());
      dispatch(resetWowData());
    } else {
      setWowQuartersError('');
      dispatch({ type: CLEAR_CAMPAIGN_LIST });

      dispatch(resetScreenData());
      dispatch(resetReportData());
      dispatch(resetCreativeData());
      dispatch(resetWowData());

      const { startDate, endDate } = getMultipleQuartersDateRange(newValue);
      const quarterRanges = newValue.map((quarter) => {
        const range = getQuarterDateRange(quarter);
        return {
          quarter,
          startDate: range.startDate,
          endDate: range.endDate
        };
      });

      console.log('WoW Quarter Selection:', {
        quarters: newValue,
        startDate,
        endDate,
        quarterRanges
      });

      dispatch({ type: SET_WOW_START_DATE, payload: startDate });
      dispatch({ type: SET_WOW_END_DATE, payload: endDate });
      dispatch({ type: SET_WOW_QUARTER_RANGES, payload: quarterRanges });

      console.log('⏳ Waiting for user to finish selecting quarters...');
      debouncedFetchCampaigns(quarterRanges, isSkanPresent);
    }

    setWowQuarterss(newValue);
    setWowQuarters(newValue);
  };

  return (
    <div ref={dropdownRef}>
      <Autocomplete
        multiple
        id="checkboxes-tags-demo"
        options={advertiser_quarters}
        disableCloseOnSelect
        disableClearable={true}
        value={wowQuarterss}
        onChange={handleChange}
        disabled={disabled}
        isOptionEqualToValue={(option, value) => option === value}
        renderOption={(props, option, { selected }) => (
          <li {...props} style={{ padding: 0, margin: 0 }}>
            <Box
              component="div"
              sx={{
                display: 'flex',
                alignItems: 'center',
                width: '100%',
                padding: '6px 16px',
                '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', minWidth: '32px' }}>
                <Checkbox
                  checked={selected}
                  onClick={(e) => e.stopPropagation()}
                  sx={{
                    padding: '4px',
                    '& .MuiSvgIcon-root': {
                      fontSize: 20,
                      color: selected ? 'primary.main' : 'grey.500'
                    }
                  }}
                />
              </Box>
              <Typography component="span" sx={{ flex: 1, lineHeight: '1.5', paddingLeft: '4px' }}>
                {option}
              </Typography>
            </Box>
          </li>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Select Quarter"
            variant="outlined"
            error={!!wowQuartersError}
            helperText={wowQuartersError}
            sx={{
              '& .MuiOutlinedInput-root': {
                '& .MuiOutlinedInput-notchedOutline': {
                  borderRadius: '8px'
                }
              }
            }}
          />
        )}
        renderTags={(value, getTagProps) => (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center' }}>
            {value.slice(0, 2).map((option, index) => (
              <Chip
                {...getTagProps({ index })}
                key={index}
                label={option}
                onDelete={(e) => {
                  e.stopPropagation();
                  const newValue = value.filter((v) => v !== option);
                  handleChange(null, newValue);
                }}
                sx={{
                  height: '30px',
                  '& .MuiChip-label': {
                    padding: '0 8px',
                    maxWidth: 'none'
                  }
                }}
              />
            ))}
            {value.length > 2 && (
              <Chip
                label={`+${value.length - 2} more`}
                onClick={handleOpenPopover}
                sx={{
                  cursor: 'pointer',
                  height: '30px',
                  '& .MuiChip-label': {
                    padding: '0 8px'
                  }
                }}
              />
            )}
            <Popover
              open={Boolean(anchorEl)}
              anchorEl={anchorEl}
              onClose={handleClosePopover}
              onClick={(e) => e.stopPropagation()}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              PaperProps={{
                sx: {
                  maxHeight: 200,
                  overflow: 'auto',
                  boxShadow: '0px 2px 8px rgba(0,0,0,0.15)'
                }
              }}
            >
              <Box sx={{ p: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {value.slice(2).map((option, index) => (
                  <Chip
                    key={index}
                    label={option}
                    onDelete={() => {
                      const newValue = value.filter((v) => v !== option);
                      handleChange(null, newValue);
                    }}
                    sx={{
                      margin: '2px',
                      height: '24px',
                      '& .MuiChip-label': {
                        padding: '0 8px'
                      }
                    }}
                  />
                ))}
              </Box>
            </Popover>
          </Box>
        )}
        ListboxProps={{
          sx: {
            maxHeight: 200,
            '& .MuiAutocomplete-option': {
              padding: 0
            }
          }
        }}
        sx={{
          width: '100%',
          '& .MuiAutocomplete-tag': {
            margin: '2px'
          },
          '& .MuiAutocomplete-clearIndicator': {
            display: 'none'
          }
        }}
      />
    </div>
  );
};

const mapStateToProps = (state) => {
  return {
    loading: state.wowReducer.loading,
    advertiser_quarters: state.wowReducer.advertiser_quarters,
    advertiser_guid: state.advertiserReducer.advertiser_guid,
    selected_wow_quarters: state.wowReducer.selected_advertiser_wow_quarters,
    isSkanPresent: state.campaignReducer.isSkan,
    campaign_list: state.advertiserReducer.campaign_list,
    startDate: state.wowReducer.wow_start_date,
    endDate: state.wowReducer.wow_end_date,
    sfId: state.advertiserReducer.sfId
  };
};

const mapDispatchToProps = {
  fetchWowAdvertiserQuarters,
  resetScreenData,
  resetReportData,
  resetCreativeData,
  resetWowData
};

export default connect(mapStateToProps, mapDispatchToProps)(WowQuarterDropDown);
